import { useState } from "react";
import { AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, useMediaQuery, Menu, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoriesAnchorEl, setCategoriesAnchorEl] = useState(null); // For categories dropdown
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Check if screen is small

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoriesClick = (event) => {
    setCategoriesAnchorEl(event.currentTarget); // Open categories dropdown
  };

  const handleCategoriesClose = () => {
    setCategoriesAnchorEl(null); // Close categories dropdown
  };

  // Define menu items
  const navItems = [
    { label: "Home", path: "/" },
    { label: "Premium Tools", path: "/tools" },
    { label: "Reviews", path: "/reviews" },
  ];

  // Define categories
  const categories = [
    { label: "Courses", path: "/courses" },
    { label: "Books", path: "/books" },
    { label: "Audio Books", path: "/audiobooks" },
    { label: "Entertainment", path: "/entertainment" },
    { label: "Anime", path: "/anime" },
    { label: "Softwares & Apps", path: "/software" },
    { label: "Games", path: "/games" },
    { label: "Wallpapers", path: "/wallpapers" },
    { label: "Islamic Collection", path: "/islamic" },
    { label: "English Language", path: "/english" },
  ];

  return (
    <>
      {/* Top Navbar */}
      <AppBar 
        position="sticky" 
        sx={{ 
          background: 'linear-gradient(45deg, #2c3e50, #3498db)', // Gradient background
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton 
              edge="start" 
              color="inherit" 
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/Brand Name */}
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700, 
              fontFamily: '"Montserrat", sans-serif', // Modern font
              fontSize: '1.5rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              textDecoration: 'none', 
              color: '#ffffff',
              transition: 'all 0.3s ease',
              '&:hover': { 
                color: '#ff9800', 
                textDecoration: 'underline',
              },
            }} 
            component={Link} 
            to="/"
          >
            Premium Collection
          </Typography>

          {/* Desktop Nav Links */}
          {!isMobile && (
            <>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  variant="text"
                  component={Link}
                  to={item.path}
                  sx={{ 
                    ml: 2, 
                    fontWeight: 600,
                    fontFamily: '"Montserrat", sans-serif', // Modern font
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#ffffff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}

              {/* Categories Dropdown */}
              <Button
                color="inherit"
                variant="text"
                onClick={handleCategoriesClick}
                sx={{ 
                  ml: 2, 
                  fontWeight: 600,
                  fontFamily: '"Montserrat", sans-serif', // Modern font
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#ffffff',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Categories
              </Button>
              <Menu
                anchorEl={categoriesAnchorEl}
                open={Boolean(categoriesAnchorEl)}
                onClose={handleCategoriesClose}
                sx={{
                  '& .MuiPaper-root': {
                    background: 'linear-gradient(45deg, #2c3e50, #3498db)',
                    color: '#ffffff',
                  },
                }}
              >
                {categories.map((category) => (
                  <MenuItem
                    key={category.label}
                    component={Link}
                    to={category.path}
                    onClick={handleCategoriesClose}
                    sx={{
                      fontFamily: '"Montserrat", sans-serif',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {category.label}
                  </MenuItem>
                ))}
              </Menu>

              {/* Buy Premium Button */}
              <Button
                color="warning"
                variant="contained"
                component={Link}
                to="/buy-premium"
                sx={{ 
                  ml: 2, 
                  fontWeight: 600,
                  fontFamily: '"Montserrat", sans-serif', // Modern font
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#ffffff',
                  backgroundColor: '#ff9800',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#ff7043',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                Buy Premium
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Sidebar Drawer */}
      <Drawer 
        anchor="left" 
        open={mobileOpen} 
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 250,
            background: 'linear-gradient(45deg, #2c3e50, #3498db)', // Gradient background
            color: '#ffffff',
          },
        }}
      >
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton 
                component={Link} 
                to={item.path} 
                onClick={handleDrawerToggle}
                sx={{
                  padding: '12px 16px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ListItemText 
                  primary={item.label} 
                  sx={{ 
                    fontFamily: '"Montserrat", sans-serif', // Modern font
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {/* Categories in Mobile Drawer */}
          <ListItem disablePadding>
            <ListItemButton 
              onClick={handleCategoriesClick}
              sx={{
                padding: '12px 16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemText 
                primary="Categories" 
                sx={{ 
                  fontFamily: '"Montserrat", sans-serif', // Modern font
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              />
            </ListItemButton>
          </ListItem>
          {categories.map((category) => (
            <ListItem key={category.label} disablePadding>
              <ListItemButton 
                component={Link} 
                to={category.path} 
                onClick={handleDrawerToggle}
                sx={{
                  padding: '12px 16px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ListItemText 
                  primary={category.label} 
                  sx={{ 
                    fontFamily: '"Montserrat", sans-serif', // Modern font
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {/* Buy Premium in Mobile Drawer */}
          <ListItem disablePadding>
            <ListItemButton 
              component={Link} 
              to="/buy-premium" 
              onClick={handleDrawerToggle}
              sx={{
                padding: '12px 16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemText 
                primary="Buy Premium" 
                sx={{ 
                  fontFamily: '"Montserrat", sans-serif', // Modern font
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#ff9800',
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;